import React, { useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';

import { queryClient } from 'api';
import { formatDistrolistMember } from 'utils/format';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';
import { Button } from '@epoch-ui/components/Button';
import { Icon } from '@epoch-ui/components/Icon';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription
} from '@epoch-ui/components/Modal/modal';
import { Separator } from '@epoch-ui/components/Separator';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { distrolistInvitesApi } from '@event-editor/api/event-invites';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { useCanAccessDistrolists } from 'hooks/useCanAccessDistrolists';

const ManageDistrolistInvitesModal: React.FC<React.ComponentProps<typeof Modal>> = ({
  open,
  onOpenChange,
  ...props
}) => {
  const event = useEditableEvent();
  const canAccessDistrolists = useCanAccessDistrolists();
  const { showToast } = useToaster();

  const distrolistInvitesQuery = useQuery({
    queryKey: eventEditorQueryKeys.eventDistrolistInvites(event.id!),
    queryFn: () => distrolistInvitesApi.list(event.id!),
    enabled: canAccessDistrolists,
    throwOnError: false
  });

  const deleteDistrolistInviteMutation = useMutation({
    mutationFn: (distrolistId: number) => distrolistInvitesApi.delete(event.id!, distrolistId),
    onSuccess: (ok) => {
      queryClient.invalidateQueries({
        queryKey: eventEditorQueryKeys.eventDistrolistInvites(event.id!)
      });
      if (ok) {
        showToast('Distrolist removed');
      } else {
        showToast('Failed to remove distrolist', { variant: ToasterVariant.ERROR });
      }
    },
    onError: () => {
      showToast('Failed to remove distrolist', { variant: ToasterVariant.ERROR });
    }
  });
  const currentRemovingDistrolistId = deleteDistrolistInviteMutation.variables;

  // Published (default)
  let descriptionText =
    'New and deleted members are syncing with the guest list automatically. Removing a distrolist will not affect guests who are already part of the event.';
  if (event.hasEnded) {
    // Past event
    descriptionText = 'New and deleted members synced with the guest list automatically.';
  } else if (!event.isPublished) {
    // Draft
    descriptionText = 'New and deleted members will be syncing with the guest list automatically.';
  }

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      // Prevent closing the modal if a distrolist is being removed
      if (deleteDistrolistInviteMutation.isPending) {
        return;
      }

      onOpenChange?.(nextOpen);
    },
    [deleteDistrolistInviteMutation.isPending, onOpenChange]
  );

  return (
    <Modal open={open} onOpenChange={handleOpenChange} {...props}>
      <ModalContent size="lg">
        <ModalHeader>
          <ModalTitle>Distrolists</ModalTitle>
          <ModalDescription>{descriptionText}</ModalDescription>
        </ModalHeader>

        <Separator className="tw-mb-4" />

        <div className="tw-max-h-[50vh] tw-flex-1 tw-overflow-y-auto tw-py-4">
          {distrolistInvitesQuery.isLoading && <LoadingSpinner />}
          {!distrolistInvitesQuery.isLoading && distrolistInvitesQuery.data?.length === 0 ? (
            <p className="tw-m-0 tw-text-center tw-text-sm tw-text-primary-grey">
              No distrolists found
            </p>
          ) : (
            <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-divide-y tw-divide-border tw-p-0">
              {distrolistInvitesQuery.data?.map((distrolist, index) => (
                <>
                  <li key={distrolist.id} className="tw-flex tw-items-center tw-gap-2">
                    <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
                      <p className="tw-m-0 tw-leading-4">{distrolist.name}</p>
                      <p className="tw-m-0 tw-leading-4">
                        {formatDistrolistMember(distrolist.memberCount)}
                      </p>
                    </div>
                    {!event.hasEnded && (
                      <Button
                        variant="destructive"
                        size="xs"
                        disabled={deleteDistrolistInviteMutation.isPending}
                        onClick={() => deleteDistrolistInviteMutation.mutate(distrolist.id)}
                        loading={
                          deleteDistrolistInviteMutation.isPending &&
                          currentRemovingDistrolistId === distrolist.id
                        }
                      >
                        <Icon name="circle-x" size={16} className="tw-mr-2" />
                        Remove
                      </Button>
                    )}
                  </li>

                  {index < (distrolistInvitesQuery.data?.length || 0) - 1 && (
                    <Separator className="tw-my-5" />
                  )}
                </>
              ))}
            </ol>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export { ManageDistrolistInvitesModal };
